import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Send, Plus, MessageSquare } from "lucide-react";

export default function ChefHelper() {
  return <HelperShell role="chef" />;
}

export function HelperShell({ role }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", message: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/support/messages");
      setThreads(data);
      if (active) {
        const refreshed = data.find((t) => t.id === active.id);
        if (refreshed) setActive(refreshed);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/support/messages", newThread);
      setNewThread({ title: "", message: "" });
      setOpen(false);
      await load();
      setActive(data);
      toast.success("Discussion ouverte");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    try {
      await api.post(`/support/messages/${active.id}/reply`, { title: active.title, message: reply });
      setReply("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Support"
        title="PAG Helper"
        sub="Posez vos questions à l'équipe support PAG. Réponses sous 24h ouvrées."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="helper-new-thread" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Nouvelle question</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">Nouvelle question à PAG Helper</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="new-thread-form">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Sujet</Label>
                  <Input required value={newThread.title} onChange={(e) => setNewThread({ ...newThread, title: e.target.value })} data-testid="new-thread-title" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Votre message</Label>
                  <Textarea required rows={6} value={newThread.message} onChange={(e) => setNewThread({ ...newThread, message: e.target.value })} data-testid="new-thread-msg" />
                </div>
                <DialogFooter><Button type="submit" data-testid="new-thread-send" className="bg-[#002FA7] text-white">Envoyer</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10 grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 bg-white border border-[#DEE2E6]" data-testid="helper-threads">
          <div className="p-4 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-[#002FA7]" /> Mes discussions</div>
          {threads.length === 0 ? <div className="p-8 text-center text-sm text-[#868E96]">Aucune discussion. Cliquez sur "Nouvelle question".</div> : threads.map((t) => (
            <button key={t.id} onClick={() => setActive(t)} data-testid={`helper-thread-${t.id}`} className={`w-full text-left p-4 border-b border-[#DEE2E6] last:border-b-0 ${active?.id === t.id ? "bg-[#002FA7]/5 border-l-4 border-l-[#002FA7]" : "hover:bg-[#F8F9FA]"}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="font-semibold text-sm truncate flex-1">{t.title}</div>
                {t.unread_for_user && <span className="h-2 w-2 bg-[#E60000] rounded-full shrink-0 mt-1.5" />}
              </div>
              <div className="text-[10px] text-[#868E96] mt-1 font-mono">{new Date(t.updated_at).toLocaleString("fr-FR")}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-8 bg-white border border-[#DEE2E6] flex flex-col min-h-[500px]" data-testid="helper-conversation">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#868E96]">
              <div className="text-center"><MessageSquare className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />Sélectionnez une discussion ou ouvrez-en une nouvelle.</div>
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-[#DEE2E6]">
                <h3 className="font-display text-lg font-bold">{active.title}</h3>
              </div>
              <div className="flex-1 p-5 space-y-3 overflow-y-auto bg-[#F8F9FA]">
                {active.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.from === role ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] p-3 ${m.from === role ? "bg-[#002FA7] text-white" : "bg-[#FFD700] text-[#0A0A0A]"}`}>
                      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">{m.from_name || (m.from === "admin" ? "PAG Helper" : "Vous")}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                      <div className="text-[10px] opacity-60 mt-1 font-mono">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-[#DEE2E6] flex gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Votre message…" rows={2} data-testid="helper-reply-input" className="flex-1" />
                <Button onClick={send} disabled={!reply.trim()} data-testid="helper-reply-send" className="bg-[#002FA7] text-white self-end h-11"><Send className="h-4 w-4 mr-2" /> Envoyer</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
